/**
 * Human readable API errors.
 *
 * The Worker reports a machine code plus structured details and, in most cases,
 * already localized copy. A few codes are only useful once the details are folded
 * into the text, and one family describes a failure the generic catalog entry
 * cannot express. Pages call one helper so the mapping lives in a single place.
 */

import type { TFunction } from 'i18next'

import type { ApiErrorCode } from '@shared/errors'

import type { ApiError } from './api'
import { formatUnixSeconds } from './format'
import { currentLocale } from './i18n'

/**
 * Translation key per `no_candidates` reason reported by the engine.
 *
 * `no_service` is a journey no timetable covers at that hour, which no other
 * reason describes; it is named so the organizer can change the time or add a
 * travel mode instead of lowering a limit that was never the problem.
 */
const REASON_KEYS: Record<string, string> = {
  catalog: 'recommend.reasonCatalog',
  time_window: 'recommend.reasonTimeWindow',
  travel_time: 'recommend.reasonTravelTime',
  poi_coverage: 'recommend.reasonPoiCoverage',
  provider_unavailable: 'recommend.reasonProviderUnavailable',
  reference_data: 'recommend.reasonReferenceData',
  last_transit: 'recommend.reasonLastTransit',
  no_service: 'recommend.reasonNoService',
  mixed: 'recommend.reasonMixed',
}

/**
 * Causes a mixed round can be made of, in the order the advice is most likely to
 * help. Each entry names one counter from the engine's `details`, the cause it
 * represents and the change that addresses it; a cause that appears twice (the
 * estimate screen and the verified measurement are both the commute) is reported
 * once.
 */
const MIXED_CAUSES: ReadonlyArray<{ field: string; cause: string; advice: string }> = [
  {
    field: 'rejectedByTravel',
    cause: 'recommend.causeTravelTime',
    advice: 'recommend.adviceTravelTime',
  },
  {
    field: 'rejectedByEstimate',
    cause: 'recommend.causeTravelTime',
    advice: 'recommend.adviceTravelTime',
  },
  {
    field: 'rejectedByCoverage',
    cause: 'recommend.causePoiCoverage',
    advice: 'recommend.advicePoiCoverage',
  },
  {
    field: 'rejectedByTimeWindow',
    cause: 'recommend.causeTimeWindow',
    advice: 'recommend.adviceTimeWindow',
  },
  {
    field: 'rejectedByLastTransit',
    cause: 'recommend.causeLastTransit',
    advice: 'recommend.adviceLastTransit',
  },
  {
    field: 'rejectedByNoService',
    cause: 'recommend.causeNoService',
    advice: 'recommend.adviceNoService',
  },
  {
    field: 'rejectedByProvider',
    cause: 'recommend.causeProvider',
    advice: 'recommend.adviceProvider',
  },
]

/**
 * Sentence for a round that several constraints each rejected part of.
 *
 * The engine reports the counters rather than a reason per district, so the
 * message is assembled from what actually happened: listing causes the round did
 * not hit would send the organizer to change the wrong thing. `null` means no
 * counter explains the failure, and the caller picks a sentence that does not
 * need one — interpolating the template without `causes`/`advice` would print
 * its placeholders.
 */
function mixedReasonText(
  details: Record<string, unknown>,
  t: TFunction,
): string | null {
  const causes: string[] = []
  const advice: string[] = []
  for (const entry of MIXED_CAUSES) {
    const count = details[entry.field]
    if (typeof count !== 'number' || !Number.isFinite(count) || count <= 0) {
      continue
    }
    const cause = t(entry.cause)
    if (!causes.includes(cause)) {
      causes.push(cause)
    }
    const hint = t(entry.advice)
    if (!advice.includes(hint)) {
      advice.push(hint)
    }
  }

  if (causes.length === 0) {
    return null
  }
  return t('recommend.reasonMixed', {
    causes: causes.join(t('common.listSeparator')),
    advice: advice.join(t('common.clauseSeparator')),
  })
}

/** Translation key per ceiling the round budget refused on. */
const BUDGET_SCOPE_KEYS: Record<string, string> = {
  event: 'events.roundBudgetEvent',
  member: 'events.roundBudgetMember',
  account_daily: 'events.roundBudgetAccountDaily',
}

/**
 * Codes whose message carries a moment in time.
 *
 * The server can only interpolate the raw value it holds, so these are formatted
 * on the client, where the visitor's locale is known.
 */
const TIMESTAMP_FIELDS: Partial<Record<ApiErrorCode, string>> = {
  invite_apply_cooldown: 'cooldownEndsAt',
  // The ban middleware reports the release moment as `until`.
  account_banned: 'until',
}

/**
 * Resolves the message for a failed request.
 *
 * `no_candidates` and `invalid_request` carry a `reason` the generic catalog
 * entry cannot express, so they are translated from the detail instead.
 */
export function apiErrorText(error: ApiError, t: TFunction): string {
  if (error.code === 'no_candidates') {
    const reason = typeof error.details.reason === 'string' ? error.details.reason : 'mixed'
    const key = REASON_KEYS[reason]
    if (key !== undefined && reason !== 'mixed') {
      return t(key)
    }
    // A reason this build does not know, or a genuinely mixed round: the
    // counters describe what happened. When even those are absent the round
    // failed for a cause no counter recorded, so the generic sentence is used
    // instead of a template whose placeholders would be printed verbatim.
    return mixedReasonText(error.details, t) ?? t('error.no_candidates')
  }

  if (error.code === 'rate_limited') {
    // The catalog message carries the wait time, so it has to be interpolated
    // rather than printed as a template.
    return t('error.rate_limited', { retryAfterSeconds: error.retryAfterSeconds ?? 60 })
  }

  if (error.code === 'round_budget_exhausted') {
    // Which ceiling was hit decides what the member can do about it, so the copy
    // is chosen from the server's scope instead of from one generic sentence.
    const scope = typeof error.details.scope === 'string' ? error.details.scope : 'event'
    const key = BUDGET_SCOPE_KEYS[scope] ?? BUDGET_SCOPE_KEYS.event
    const limit =
      scope === 'member'
        ? error.details.memberLimit
        : scope === 'account_daily'
          ? error.details.accountDailyLimit
          : error.details.eventLimit
    const used =
      scope === 'member'
        ? error.details.memberUsed
        : scope === 'account_daily'
          ? error.details.accountDailyUsed
          : error.details.eventUsed
    return t(key as string, { limit, used })
  }

  const field = TIMESTAMP_FIELDS[error.code]
  if (field) {
    const value = error.details[field]
    if (typeof value === 'number' && Number.isFinite(value)) {
      return t(`error.${error.code}`, { date: formatUnixSeconds(value, currentLocale()) })
    }
  }

  if (error.code === 'invalid_request' && error.details.reason === 'no_participants') {
    return t('events.triggerRecommendHint')
  }

  if (error.code === 'invalid_request' && error.details.reason === 'unassigned_participants') {
    // Naming the members is what makes this actionable: only they can add the
    // departure point the round needs. The list separator comes from the
    // catalog, because an English sentence must not join names with `、`.
    const missing = Array.isArray(error.details.missing)
      ? error.details.missing.filter((name): name is string => typeof name === 'string')
      : []
    return t('events.unassignedParticipants', {
      count: missing.length,
      names: missing.join(t('common.listSeparator')),
    })
  }

  // The server already localized its message, and KV may have reworded it
  // without a deploy, so a non-empty one wins over the bundled catalog. An empty
  // message means the client synthesized the error (a non-JSON response): the
  // catalog then supplies the localized `error.<code>` copy.
  return error.message.length > 0 ? error.message : t(`error.${error.code}`)
}
